import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {buildCpWidgetForm} from '../../../../../service/domain/cp-widget-column';

/**
 * navItem "Provisionssätze" of the Provisionszeile OBC, widget alias
 * `cp.provisionContract.contractAgents.table`.
 *
 * The `cp.ProvisionContractAgent` rows this event time slice fanned out into — one per
 * Provisionsart and per Abrechnungskonto. This is the pane that makes the 1:N relation visible
 * from the parent side; the same rows are reachable from the Abrechnungskonto side on the navItem
 * "Verträge / Haftungen", filtered by agent instead of by contract.
 *
 * The `source` column is deliberately part of the curated layout: a MANUAL row is never
 * overwritten by a recalculation, so telling AUTOMATIC and MANUAL apart is the first question on
 * this list.
 *
 * THE "Abrechnungskonto" COLUMN GOES TWO HOPS. `cp.ProvisionAgent` carries no name — the table
 * `cp_provision_agent` has no such column — so the obvious `pa.name` projects a column that does
 * not exist and the column renders empty. The name of an Abrechnungskonto IS the name of the
 * Berater behind it, so the query joins `provisionAgent -> agent` and takes `ag.name`. Same defect,
 * same root cause as the `provisionAgentDtoName` resolution on
 * {@link CpProvisionContractAgentBaseDataComponent}; only the remedy differs, because a QL table
 * can express the hop as a join while a detail pane has to read it.
 *
 * The liability columns are shown as the run left them. An EMPTY liability status on a MANUAL row
 * means the calculation is still outstanding — the field-level statement for that lives on the
 * Provisionssatz OBC (navItem "Haftung"), where a single row has room for the explicit hint;
 * a worklist column is not the place to repeat a sentence per cell.
 *
 * REFRESH: the rows are written by the calculation run, hence the `status$` subscription with an
 * in-place reload, plus `objectChangeEvent` for the manual dialogs.
 */
@Component({
    selector: 'cp-provision-contract-contract-agents',
    templateUrl: './cp-provision-contract-contract-agents.component.html',
    styleUrls: ['./cp-provision-contract-contract-agents.component.scss'],
    standalone: false
})
export class CpProvisionContractContractAgentsComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionContractId: number;

    contractAgentsWidget: WidgetData;

    /** Is a cp job currently running? */
    jobRunning: boolean;

    /** The mounted widget instance — only for the in-place reload (keeps sorting and scroll). */
    @ViewChild('contractAgentsWidgetRef') contractAgentsWidgetComponent: MvsWidgetComponent;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;
    protected objectChangeSubscription: Subscription;

    constructor(
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /** Reload as soon as a Provisionssatz was written. */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionContractAgent') {
                this.reloadInPlace();
            }
        });
    }

    /** Reload on the running -> not-running transition of a cp job. */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.reloadInPlace();
            }
        });
    }

    /** Reloads the table in place; builds one only if none exists yet. */
    protected reloadInPlace(): void {
        if (this.contractAgentsWidgetComponent) {
            this.contractAgentsWidgetComponent.refreshWidget();
            return;
        }
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.provisionContractId) {
            this.contractAgentsWidget = null;
            this.initialized = true;
            return;
        }

        const qlQuery: QlQueryDto = {
            name: 'cp.provisionContract.contractAgents',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractAgent',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'provisionTypeComponent', as: 'ptc', joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        // TWO hops on purpose. `cp_provision_agent` has NO name column at all
                        // (id, agent_id, entity_status, reserve_percentage, reserve_max_amount,
                        // comment, audit), so projecting `pa.name` selects a column that does not
                        // exist. The Abrechnungskonto's label is the name of the Berater behind
                        // it, so the query joins through to `am.Agent` and takes `ag.name`.
                        type: 'entity', name: 'provisionAgent', as: 'pa', joinType: 'left',
                        joins: [
                            {
                                type: 'entity', name: 'agent', as: 'ag', joinType: 'left',
                                fields: [{name: 'name'}]
                            }
                        ]
                    },
                    {
                        type: 'entity', name: 'liabilityType', as: 'lt', joinType: 'left',
                        fields: [{name: 'name'}]
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionContractId',
                    FilterCriteria.cOperatorEqual, this.provisionContractId)
            ],
            sortings: [
                new Sorting('e.id', true)
            ]
        };

        /**
         * AI: Provisionssätze of one Provisionszeile.
         *     Source: QL view on cp.ProvisionContractAgent, joined to Provisionsart,
         *     Abrechnungskonto and Haftungstyp. The source column separates the rows the run owns
         *     from the manually created ones it must leave standing.
         */
        this.contractAgentsWidget = WidgetFactory.createWidgetTableQl(
            'cp.provisionContract.contractAgents.table',
            'Provisionssätze zu dieser Provisionszeile',
            qlQuery,
            WidgetDataParam.create('paging', 25),
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'ptc.name', label: 'Provisionsart'},
                {field: 'ag.name', label: 'Abrechnungskonto'},
                {field: 'e.agentRole', label: 'Beraterrolle'},
                {field: 'e.provisionAmount', label: 'Betrag'},
                {field: 'e.provisionAmountOverwrite', label: 'Overwrite'},
                {field: 'e.totalAmount', label: 'Gesamtbetrag'},
                {field: 'lt.name', label: 'Haftungstyp'},
                {field: 'e.liabilityStatus', label: 'Haftungsstatus'},
                {field: 'e.inLiabilityAmount', label: 'In Haftung'},
                {field: 'e.source', label: 'Quelle'}
            ]))
        );

        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionContractId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
